class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isLoading) {
            this.loadNextPage();
          }
        });
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
  }

  connectedCallback() {
    if (this.hasAttribute('data-url')) {
      this.intersectionObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.intersectionObserver.disconnect();
  }

  /**
   * Resolve the Shopify section ID from the closest shopify-section wrapper.
   */
  getSectionId() {
    const shopifySection = this.closest('.shopify-section');
    if (shopifySection) {
      return shopifySection.id.replace('shopify-section-', '');
    }
    return null;
  }

  async loadNextPage() {
    const rawUrl = this.getAttribute('data-url');
    if (!rawUrl) return;

    this.isLoading = true;

    // Show a loading spinner
    this.innerHTML = `
      <div style="display: flex; justify-content: center; padding: 2rem; width: 100%;">
        <style>
          @keyframes infinite-scroll-spin { 100% { transform: rotate(360deg); } }
        </style>
        <svg viewBox="0 0 24 24" style="animation: infinite-scroll-spin 1s linear infinite; width: 24px; height: 24px; color: currentColor;">
          <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="30" stroke-dashoffset="0" stroke-linecap="round"></circle>
        </svg>
      </div>
    `;

    try {
      // Build URL with section_id for reliable section rendering
      const url = new URL(rawUrl, window.location.origin);
      url.hash = ''; // strip any hash (e.g. #collection)
      const sectionId = this.getSectionId();
      if (sectionId) {
        url.searchParams.set('section_id', sectionId);
      }

      const response = await fetch(url.toString());
      const text = await response.text();
      const tempDoc = new DOMParser().parseFromString(text, 'text/html');

      // Locate the product list in the fetched section
      const newProductList = tempDoc.querySelector('product-list');
      const currentProductList = document.querySelector('.collection__inner product-list');

      if (newProductList && currentProductList) {
        // Collect all child nodes into a static array first, excluding scripts/styles/links
        const newItems = Array.from(newProductList.children).filter(
          (el) => !['script', 'style', 'link'].includes(el.tagName.toLowerCase())
        );

        newItems.forEach((child, index) => {
          // Remove reveal-on-scroll since we will handle animation manually
          child.removeAttribute('reveal-on-scroll');
          child.querySelectorAll('[reveal-on-scroll]').forEach((el) => {
            el.removeAttribute('reveal-on-scroll');
          });

          const importedNode = document.importNode(child, true);
          currentProductList.appendChild(importedNode);

          const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (!prefersReducedMotion) {
            // Apply a staggered fade-up animation to match the theme's default reveal
            importedNode.animate(
              [
                { opacity: 0, transform: 'translateY(20px)' },
                { opacity: 1, transform: 'translateY(0)' }
              ],
              {
                duration: 500,
                easing: 'cubic-bezier(0.25, 1, 0.5, 1)', // smooth ease-out
                delay: index * 50, // 50ms stagger per item
                fill: 'both' // ensure it stays at opacity: 1
              }
            );
          } else {
            importedNode.style.opacity = '1';
          }
        });
      }

      // Determine if there's another page to load
      const nextTrigger = tempDoc.querySelector('infinite-scroll');
      if (nextTrigger && nextTrigger.hasAttribute('data-url')) {
        this.setAttribute('data-url', nextTrigger.getAttribute('data-url'));
        this.innerHTML = ''; // clear spinner
        this.isLoading = false;
      } else {
        // No more pages — clean up
        this.intersectionObserver.disconnect();
        this.remove();
      }
    } catch (error) {
      console.error('[InfiniteScroll] Error fetching next page:', error);
      this.innerHTML = '';
      this.isLoading = false;
    }
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}
